/** Profile → Tokens & installations: the credentials, and what they bound.
 *
 * Tokens are for loggers, not for people — nothing on this screen signs
 * anybody in, and the portal itself never sends one. What a token does is let
 * one installation of the datalogger reach this service with a fixed list of
 * things it may send, which is why the screen puts the credentials that exist
 * and the installations they produced in one column, and the way to make
 * another in the next.
 *
 * The one moment that matters is creation. `POST /api/tokens` is the only
 * reply in the whole service that carries a plaintext secret, and nothing
 * will produce it again, so the reveal card is rendered from that reply held
 * in state rather than from the refreshed list — the list has no secret in it
 * and never will. It stays until the screen is left, and the card says so.
 *
 * Capabilities are read here because the scopes on offer are the server's
 * decision: a token scoped to a type the admin has switched off would be
 * refused by every endpoint it was made for, and `POST /api/tokens` answers
 * `403 type_disabled` rather than issuing one. Reading the same document the
 * logger reads keeps both ends offering the same list.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAccount,
  issueToken,
  listAccountTokens,
  listInstallations,
  readCapabilities,
  renameInstallation,
  revokeAccountToken,
} from '@/lib/api';
import type { Account, DataType, NewToken, Scope, Source, TokenSummary } from '@/lib/types';
import { ErrorNote, LoadingPanel, Page } from '@/components/ui';
import {
  ConfirmDialog,
  CreateTokenCard,
  InstallationTable,
  ProfileHeader,
  TokenReveal,
  TokenTable,
} from '@/components/profile';

/** The three the portal knows how to describe. A type the server grows later
 *  is left alone here rather than offered with no words beside it. */
const KNOWN_TYPES: readonly DataType[] = ['tracks', 'sessions', 'live'];

export default function ProfileTokens() {
  const [account, setAccount] = useState<Account | null>(null);
  const [tokens, setTokens] = useState<TokenSummary[] | null>(null);
  const [maxTokens, setMaxTokens] = useState<number | null>(null);
  const [sources, setSources] = useState<Source[] | null>(null);
  const [enabledTypes, setEnabledTypes] = useState<readonly DataType[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  /** The one reply with a secret in it, kept for as long as this screen is up. */
  const [revealed, setRevealed] = useState<NewToken | null>(null);

  const [revoking, setRevoking] = useState<TokenSummary | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<unknown>(null);

  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [document, page, installations] = await Promise.all([
        getAccount(),
        listAccountTokens(),
        listInstallations(),
      ]);
      if (!live.current) return;
      setAccount(document);
      setTokens(page.tokens);
      setMaxTokens(page.max);
      setSources(installations);
    } catch (thrown) {
      if (live.current) setError(thrown);
      return;
    }

    // Capabilities are advice about which boxes to draw, not content: a
    // failure leaves all three offered and the card says why.
    try {
      const capabilities = await readCapabilities();
      if (!live.current) return;
      setEnabledTypes(KNOWN_TYPES.filter((type) => capabilities.types[type] !== undefined));
    } catch {
      if (live.current) setEnabledTypes(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(async (label: string, scopes: Scope[]) => {
    const made = await issueToken({ label, scopes });
    if (!live.current) return;
    setRevealed(made);
    // The list is re-read rather than appended to: the server decides the
    // order and holds the metadata this reply does not carry.
    const page = await listAccountTokens();
    if (!live.current) return;
    setTokens(page.tokens);
    setMaxTokens(page.max);
  }, []);

  const confirmRevoke = useCallback(() => {
    if (!revoking) return;
    setRevokeBusy(true);
    setRevokeError(null);
    void revokeAccountToken(revoking.id)
      .then((revoked) => {
        if (!live.current) return;
        setTokens((held) =>
          held ? held.map((token) => (token.id === revoked.id ? revoked : token)) : held,
        );
        // Whatever is on screen, it is not a working credential any more.
        setRevealed((shown) => (shown && shown.id === revoked.id ? null : shown));
        setRevoking(null);
      })
      .catch((thrown: unknown) => {
        if (live.current) setRevokeError(thrown);
      })
      .finally(() => {
        if (live.current) setRevokeBusy(false);
      });
  }, [revoking]);

  const rename = useCallback(async (sourceId: string, label: string) => {
    const saved = await renameInstallation(sourceId, label);
    if (!live.current) return;
    setSources((held) =>
      held ? held.map((source) => (source.source_id === saved.source_id ? saved : source)) : held,
    );
  }, []);

  if (error) {
    return (
      <Page max={1200}>
        <ErrorNote error={error} title="could not load your tokens" onRetry={() => void load()} />
      </Page>
    );
  }

  if (!account || tokens === null || sources === null) {
    return (
      <Page max={1200}>
        <LoadingPanel label="loading your tokens" />
      </Page>
    );
  }

  const liveTokens = tokens.filter((token) => token.revoked_at === null).length;
  const atLimit = maxTokens !== null && liveTokens >= maxTokens;

  return (
    <Page max={1200} className="flex flex-col gap-5">
      <ProfileHeader user={account.user} stats={account.stats} />

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(300px,1fr)]">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 12.5,
              color: 'var(--color-neutral-500)',
              textWrap: 'pretty',
            }}
          >
            Tokens are for loggers, not people. One per installation, scoped to what it may send.
          </p>

          <TokenTable
            tokens={tokens}
            revoking={revokeBusy && revoking ? revoking.id : null}
            onRevoke={(token) => {
              setRevoking(token);
              setRevokeError(null);
            }}
          />

          {revealed ? <TokenReveal token={revealed} /> : null}

          <section style={{ minWidth: 0 }}>
            <h2
              style={{
                fontSize: 13,
                color: 'var(--color-neutral-500)',
                margin: '8px 0 6px',
              }}
            >
              Installations bound to this account
            </h2>
            <InstallationTable sources={sources} onRename={rename} />
            <p
              style={{
                fontSize: 11,
                color: 'var(--color-neutral-600)',
                margin: '8px 0 0',
                textWrap: 'pretty',
              }}
            >
              A source id belongs to the first account that uploads it, forever — that is how
              corroboration is counted per person rather than per install. Renaming one changes
              nothing but this table.
            </p>
          </section>
        </div>

        <CreateTokenCard
          enabledTypes={enabledTypes}
          onCreate={create}
          atLimit={atLimit}
          limit={maxTokens ?? undefined}
        />
      </div>

      {revoking ? (
        <ConfirmDialog
          title={`Revoke “${revoking.label}”?`}
          confirmLabel="Revoke token"
          busy={revokeBusy}
          error={revokeError}
          onConfirm={confirmRevoke}
          onCancel={() => {
            setRevoking(null);
            setRevokeError(null);
          }}
        >
          That installation stops being able to upload the moment this is done, and the token
          cannot be brought back. Everything it has already sent stays where it is. To start it
          again, make a new token and paste its connection string into the logger.
        </ConfirmDialog>
      ) : null}
    </Page>
  );
}
